import { UnsavedChangesModal, type UnsavedChangesModalProps } from '@app/components'
import { showMessagesNotifications, useAttestationPeriod, useSaveAttestationPeriod } from '@app/lib'
import { type SugarStudentAttestation } from '@app/types'
import { Anchor, Button, Center, Checkbox, createStyles, Group, Table, Tooltip } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm, yupResolver } from '@mantine/form'
import dayjs from 'dayjs'
import { filter, map } from 'lodash'
import { IconLock } from '@tabler/icons-react'
import { type FC, useCallback, useEffect } from 'react'
import * as yup from 'yup'

const mapStudentAttestations = (studentAttestations: SugarStudentAttestation[]) =>
  map(studentAttestations, ({ attended, enrollmentEndDate, enrollmentId, studentAttestationId }) => ({
    attended: attended || false,
    enrollmentEndDate: enrollmentEndDate ? dayjs(enrollmentEndDate).toDate() : null,
    enrollmentId,
    studentAttestationId
  }))
const useStyles = createStyles(() => ({
  clearDisabled: {
    '&:disabled': {
      backgroundColor: 'unset',
      color: 'unset'
    },
    '&[data-disabled]': {
      backgroundColor: 'unset',
      color: 'unset'
    }
  },
  fitWidth: {
    width: 1,
    whiteSpace: 'nowrap'
  },
  lockColumn: {
    width: 30
  }
}))

type FormData = {
  studentAttestations: {
    attended: boolean
    enrollmentEndDate: Date | null
    enrollmentId: string
    studentAttestationId: string | null
  }[]
}

export interface AttestationModalProps extends Omit<UnsavedChangesModalProps, 'children' | 'isChanged' | 'isLoading'> {
  attestationPeriodId?: string | null
}

export const AttestationModal: FC<AttestationModalProps> = ({ attestationPeriodId, onClose, ...props }) => {
  const { classes } = useStyles()
  const { data: attestation, isFetching: isFetchingAttestation } = useAttestationPeriod(attestationPeriodId)
  const { mutateAsync: saveAttestation, isPending: isSavingAttestation } = useSaveAttestationPeriod()
  const isLocked = attestation?.attestationLocked ?? false
  const form = useForm<FormData>({
    initialValues: {
      studentAttestations: attestation?.studentAttestations
        ? mapStudentAttestations(attestation.studentAttestations)
        : []
    },
    validate: yupResolver(
      yup.object({
        studentAttestations: yup.array(
          yup.object({
            attended: yup.boolean(),
            enrollmentEndDate: yup.date().nullable(),
            enrollmentId: yup.string(),
            studentAttestationId: yup.string().nullable()
          })
        )
      })
    ),
    validateInputOnChange: true
  })

  const handleClose = useCallback(() => {
    onClose()
    form.reset()
  }, [form, onClose])
  const toggleAttended = useCallback(
    (attended: boolean) => {
      form.setValues((values) => ({
        studentAttestations: map(values.studentAttestations, (studentAttestation, idx) => ({
          ...studentAttestation,
          attended: attestation?.studentAttestations[idx]?.paymentLocked ? studentAttestation.attended : attended
        }))
      }))
    },
    [attestation?.studentAttestations, form]
  )
  const handleSubmit = useCallback(async () => {
    if (attestation) {
      const editableStudentAttestations = filter(
        form.values.studentAttestations,
        (_, idx) => !attestation.studentAttestations[idx]?.paymentLocked
      )
      const { messages, success } = await saveAttestation({
        attestationId: attestation.attestationId,
        attestationPeriodId: attestation.attestationPeriodId,
        studentAttestations: map(editableStudentAttestations, ({ enrollmentEndDate, ...studentAttestation }) => ({
          ...studentAttestation,
          enrollmentEndDate: enrollmentEndDate ? dayjs(enrollmentEndDate).format('YYYY-MM-DD') : null
        }))
      })

      if (success) {
        handleClose()
      }

      showMessagesNotifications(messages)
    }
  }, [attestation, form.values.studentAttestations, handleClose, saveAttestation])
  const isDirty = form.isDirty()
  const isValid = form.isValid()

  useEffect(() => {
    if (attestation?.studentAttestations) {
      const values: FormData = {
        studentAttestations: mapStudentAttestations(attestation.studentAttestations)
      }

      form.setValues(values)
      form.resetDirty(values)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attestation?.studentAttestations])

  return (
    <UnsavedChangesModal
      fullScreen
      isChanged={isDirty}
      isLoading={isSavingAttestation || isFetchingAttestation}
      onClose={handleClose}
      {...props}
    >
      <Table
        horizontalSpacing='xs'
        mb='lg'
        verticalSpacing='xs'
        withBorder
        withColumnBorders
      >
        <thead>
          <tr>
            <th className={classes.lockColumn} />
            <th>Student Name</th>
            <th className={classes.fitWidth}>Enrollment Start</th>
            <th className={classes.fitWidth}>Enrollment End</th>
            <th className={classes.fitWidth}>Provider</th>
            <th className={classes.fitWidth}>
              <Anchor onClick={useCallback(() => toggleAttended(true), [toggleAttended])}>All</Anchor> /{' '}
              <Anchor onClick={useCallback(() => toggleAttended(false), [toggleAttended])}>None</Anchor>
            </th>
          </tr>
        </thead>
        <tbody>
          {map(attestation?.studentAttestations, (studentAttestation, studentAttestationIdx) => {
            const isPaymentLocked = studentAttestation.paymentLocked ?? false
            const disabled = isSavingAttestation || studentAttestation.locked || isPaymentLocked

            return (
              <tr key={`student-attestation-${studentAttestationIdx}`}>
                <td className={classes.lockColumn}>
                  {isPaymentLocked ? (
                    <Tooltip label='Linked to a locked payment'>
                      <IconLock size={14} />
                    </Tooltip>
                  ) : null}
                </td>
                <td>
                  {studentAttestation.studentLastName}, {studentAttestation.studentFirstName}
                </td>
                <td className={classes.fitWidth}>
                  {dayjs(studentAttestation.enrollmentStartDate).format('MMMM D, YYYY')}
                </td>
                <td className={classes.fitWidth}>
                  <DateInput
                    classNames={{
                      input: classes.clearDisabled
                    }}
                    disabled={disabled || isLocked}
                    miw={160}
                    variant='default'
                    {...form.getInputProps(`studentAttestations.${studentAttestationIdx}.enrollmentEndDate`)}
                  />
                </td>
                <td className={classes.fitWidth}>{studentAttestation.providerName ?? '-'}</td>
                <td className={classes.fitWidth}>
                  <Center>
                    <Checkbox
                      classNames={{
                        input: classes.clearDisabled
                      }}
                      disabled={disabled || isLocked}
                      size='sm'
                      {...form.getInputProps(`studentAttestations.${studentAttestationIdx}.attended`, {
                        type: 'checkbox'
                      })}
                    />
                  </Center>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
      {!isLocked && (
        <Group position='right'>
          <Button
            disabled={!isDirty || !isValid || isFetchingAttestation || isSavingAttestation}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Group>
      )}
    </UnsavedChangesModal>
  )
}
